import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterModule,
  Routes,
} from "@angular/router";
import { take } from "rxjs/operators";
import { LoggedInGuard } from "../auth/auth.guards";
import { BeaconsResolve } from "../beacons/beacons.routes";
import { SessionService } from "../session.service";
import { BeaconRegionComponent } from "./beacon-region/beacon-region.component";
import { BeaconRegionsListComponent } from "./beacon-regions-list/beacon-regions-list.component";
import { BeaconRegionsService } from "./beacon-regions.service";
import {
  BeaconRegionDetailGuard,
  BeaconRegionListGuard,
  BeaconRegionWriteGuard,
} from "./beacon-regions.guards";
import { BeaconsListGuard } from "../beacons/beacons.guards";
import { PermissionService } from "../permission.service";

@Injectable({ providedIn: "root" })
export class BeaconRegionsResolve implements Resolve<any> {
  constructor(
    private BeaconRegionsService: BeaconRegionsService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    const facilityIDs = this.SessionService.selectedFacilities?.length
      ? this.SessionService.selectedFacilities!.map((facility) => {
          return facility.id;
        })
      : [];

    return this.BeaconRegionsService.getBeaconRegions(
      this.SessionService.currentAccountValue!.accountID,
      { facility_id: facilityIDs, page: 0, perPage: 999999999 },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BeaconRegionResolve implements Resolve<any> {
  constructor(
    private BeaconRegionsService: BeaconRegionsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.BeaconRegionsService.getBeaconRegion(
      this.SessionService.currentAccountValue!.accountID,
      route.params.beaconRegionID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
class BeaconRegionsListPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.beaconRegions.hasWrite(),
      this.PermissionService.categories.beaconRegions.hasRemove(),
      this.PermissionService.categories.beaconRegions.hasDetail(),
      this.PermissionService.categories.beacons.hasList(),
    ]).then(
      ([
        hasBeaconRegionWrite,
        hasBeaconRegionRemove,
        hasBeaconRegionDetail,
        hasBeaconList,
      ]) => {
        return {
          hasBeaconRegionWrite,
          hasBeaconRegionRemove,
          hasBeaconRegionDetail,
          hasBeaconList,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
class BeaconRegionPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.beaconRegions.hasRemove(),
    ]).then(([hasBeaconRegionRemove]) => {
      return {
        hasBeaconRegionRemove,
      };
    });
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "infrastructure" },
    children: [
      {
        path: "list",
        component: BeaconRegionsListComponent,
        resolve: {
          // beaconRegions: BeaconRegionsResolve,
          permissions: BeaconRegionsListPermissionsResolve,
        },
        data: { title: "Beacon Regions" },
        canActivate: [BeaconRegionListGuard],
      },
      {
        path: "create",
        component: BeaconRegionComponent,
        resolve: {
          beacons: BeaconsResolve,
          permissions: BeaconRegionPermissionsResolve,
        },
        canActivate: [BeaconsListGuard, BeaconRegionWriteGuard],
        data: { title: "Create New Beacon Region" },
      },
      {
        path: ":beaconRegionID",
        resolve: {
          beaconRegion: BeaconRegionResolve,
          beacons: BeaconsResolve,
          permissions: BeaconRegionPermissionsResolve,
        },
        canActivate: [
          BeaconsListGuard,
          BeaconRegionDetailGuard,
          BeaconRegionWriteGuard,
        ],
        runGuardsAndResolvers: "paramsChange",
        children: [
          {
            path: "",
            component: BeaconRegionComponent,
            data: { title: "Beacon Region" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class BeaconRegionsRoutingModule {}

const beaconRegionsModuleLazyLoadRoutes: Routes = [
  {
    path: "beacon-regions",
    loadChildren: () =>
      import("./beacon-regions.module").then((m) => m.BeaconRegionsModule),
  },
];

export { beaconRegionsModuleLazyLoadRoutes };
